import { useState, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Volume2, VolumeX, Loader2, Settings } from "lucide-react";
import { toast } from "sonner";

// TTS Voices
const VOICES = [
  { id: "alloy", name: "Alloy", description: "Neutral", gender: "neutral" },
  { id: "ash", name: "Ash", description: "Clear", gender: "male" },
  { id: "coral", name: "Coral", description: "Warm", gender: "female" },
  { id: "echo", name: "Echo", description: "Smooth", gender: "male" },
  { id: "fable", name: "Fable", description: "Expressive", gender: "female" },
  { id: "nova", name: "Nova", description: "Energetic", gender: "female" },
  { id: "onyx", name: "Onyx", description: "Deep", gender: "male" },
  { id: "sage", name: "Sage", description: "Wise", gender: "neutral" },
  { id: "shimmer", name: "Shimmer", description: "Cheerful", gender: "female" },
];

export function ReadAloudButton({ text, size = "sm", variant = "ghost", className = "" }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    return localStorage.getItem("happybook_tts_voice") || "nova";
  });
  const audioRef = useRef(null);

  const speak = async () => {
    if (isPlaying) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      return;
    }

    if (!text?.trim()) {
      toast.error("No text to read");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/tts/speak`, {
        text: text.substring(0, 4096),
        voice: selectedVoice,
        speed: 1.0,
      });

      const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
      audioRef.current = audio;

      audio.onended = () => {
        setIsPlaying(false);
        audioRef.current = null;
      };

      audio.onerror = () => {
        toast.error("Failed to play audio");
        setIsPlaying(false);
        audioRef.current = null;
      };

      setIsPlaying(true);
      audio.play();
    } catch (error) {
      toast.error("Failed to generate speech");
    } finally {
      setIsLoading(false);
    }
  };

  const selectVoice = (voiceId) => {
    setSelectedVoice(voiceId);
    localStorage.setItem("happybook_tts_voice", voiceId);
    setShowVoiceSelector(false);
  };

  const previewVoice = async (voiceId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`${API}/tts/preview/${voiceId}`);
      const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
      audio.play();
    } catch (error) {
      toast.error("Failed to preview voice");
    }
  };

  return (
    <>
      <div className={`inline-flex items-center gap-1 ${className}`}>
        <Button
          variant={variant}
          size={size}
          onClick={speak}
          disabled={isLoading}
          className="gap-1"
          data-testid="read-aloud-btn"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isPlaying ? (
            <VolumeX className="w-4 h-4 text-amber-500" />
          ) : (
            <Volume2 className="w-4 h-4" />
          )}
          {size !== "icon" && (isPlaying ? "Stop" : "Listen")}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setShowVoiceSelector(true)}
          data-testid="voice-selector-btn"
        >
          <Settings className="w-3 h-3" />
        </Button>
      </div>

      <Dialog open={showVoiceSelector} onOpenChange={setShowVoiceSelector}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Volume2 className="w-5 h-5" />
              Choose Voice
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto py-2">
            {VOICES.map((voice) => (
              <button
                key={voice.id}
                onClick={() => selectVoice(voice.id)}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  selectedVoice === voice.id
                    ? "bg-amber-100 border-2 border-amber-500"
                    : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                }`}
              >
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{voice.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {voice.gender}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">{voice.description}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => previewVoice(voice.id, e)}
                >
                  <Volume2 className="w-3 h-3" />
                </Button>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReadAloudButton;
